import { useState } from 'react';

/**
 * Rithmic / OMNE attribution (conformance requirement, received 2026-07-18): the artwork +
 * copyright notices must be displayed on visible windows whenever the software uses Rithmic's
 * software (i.e. from the moment a user connects to any Rithmic deployment). Mounted in the
 * Rithmic connect surfaces (always — they ARE the Rithmic windows) and app-wide while a Rithmic
 * session is engaged.
 *
 * ARTWORK: Rithmic's supplied art files live in `public/rithmic/`, with a light + dark variant per
 * badge that swap with the theme (`--canvas-raised` flips light↔dark), so the wordmarks stay legible
 * either way:
 *   - light: `powered-by-omne.png` (blue) · `trading-platform-by-rithmic.png` (bold black)
 *   - dark:  `powered-by-omne-dark.png` (white) · `trading-platform-by-rithmic-dark.png` (light gray)
 * Each badge falls back to styled ™ text via `onError` if a file is missing.
 *
 * SCALING (2026-07-24 re-pick from Rithmic's official artwork pack): within each badge the light and
 * dark files are the SAME rendition at the SAME pixel dimensions, so the badge keeps ONE aspect ratio
 * and never changes width when the theme flips — previously the light Trading-Platform file was a
 * different rendition (759×82, ratio 9.26) from the dark one (863×115, ratio 7.5), so the badge
 * resized on theme change. Both badges are now sourced ABOVE their rendered height (Rithmic 863×115,
 * OMNE 230×35) so every display size DOWNscales — the old 141×15 OMNE files were upscaled ~2× to
 * `h-4` and rendered blurry. Only ever set a height (`h-*`) + `w-auto`; never a fixed width.
 */

/**
 * The exact notice lines Rithmic requires wherever copyright notices are displayed. Rithmic's
 * conformance email (2026-07-21) asks for the **four applicable** trademark/copyright notices when
 * using one API — two software copyrights (R|Protocol API + OMNE) and two trademarks (Trading Platform
 * by Rithmic + Powered by OMNE). The "Trading Platform by Rithmic™" trademark line was previously
 * missing (only 3 lines). TODO(rithmic): confirm the exact wording + year against Rithmic's conformance
 * packet before the review — the strings below follow Rithmic's standard notice format.
 */
/**
 * The four required notices, addressable BY NAME — surfaces order them differently and some render a
 * mark's ARTWORK in place of (or beside) its name, so a positional array isn't enough. This object is
 * the single source of that text: the footer used to keep its own divergent copy, which is how the
 * Trading-Platform trademark notice came to be missing there and how its OMNE line kept reading
 * "OMNE art …" (both fixed 2026-07-24).
 *
 * `omneTrademarkTail` is the OMNE trademark notice MINUS the mark's name, because Rithmic's
 * 2026-07-24 review specified that notice as "[Powered by OMNE artwork] is a trademark of …" — the
 * artwork is the sentence's subject, so the words must not repeat it.
 */
export const RITHMIC_NOTICE = {
  apiCopyright: 'The R | Protocol API™ software is Copyright © 2026 by Rithmic, LLC. All rights reserved.',
  tradingPlatformTrademark: 'Trading Platform by Rithmic™ is a trademark of Rithmic, LLC. All rights reserved.',
  omneCopyright: 'The OMNE™ software is Copyright © 2026 by Omnesys, LLC and Omnesys Technologies, Inc. All rights reserved.',
  omneTrademarkTail: 'is a trademark of Omnesys, LLC and Omnesys Technologies, Inc. All rights reserved.',
} as const;

/**
 * All four notices as plain text — for hover titles and any text-only surface. Derived from
 * {@link RITHMIC_NOTICE} so it can never drift from what the footer renders.
 */
export const RITHMIC_COPYRIGHT_LINES = [
  RITHMIC_NOTICE.apiCopyright,
  RITHMIC_NOTICE.tradingPlatformTrademark,
  RITHMIC_NOTICE.omneCopyright,
  `Powered by OMNE™ ${RITHMIC_NOTICE.omneTrademarkTail}`,
] as const;

const COPYRIGHT_TITLE = RITHMIC_COPYRIGHT_LINES.join('\n');

function ArtworkBadge({ srcLight, srcDark, alt, heightClass, inline = false }: {
  srcLight: string; srcDark: string; alt: string; heightClass: string;
  /** Flow the artwork INSIDE a sentence (baseline-aligned) instead of as a block badge in the row. */
  inline?: boolean;
}) {
  const [imgOk, setImgOk] = useState(true);
  if (!imgOk) {
    // Fallback if a file is missing — legible ™ text, not tiny.
    return <span className={`whitespace-nowrap text-[12px] font-medium leading-4 text-text-muted${inline ? ' align-middle' : ''}`}>{alt}</span>;
  }
  // Render both variants; CSS shows the one matching the theme (`[data-theme="dark"]` = Tailwind dark:).
  // `w-auto` + a height-only class keeps the supplied artwork's aspect ratio (never distort it).
  const shared = `${heightClass} w-auto`;
  return (
    <>
      <img
        src={srcLight}
        alt={alt}
        className={inline ? `${shared} inline-block align-middle dark:hidden` : `${shared} dark:hidden`}
        onError={() => setImgOk(false)}
      />
      <img
        src={srcDark}
        alt={alt}
        className={inline ? `hidden ${shared} align-middle dark:inline-block` : `hidden ${shared} dark:block`}
        onError={() => setImgOk(false)}
      />
    </>
  );
}

/**
 * The "Powered by OMNE™" artwork on its own, rendered INLINE inside a sentence — for the footer's
 * trademark notice, which Rithmic's conformance review (2026-07-24) asked to read as
 * "<artwork> is a trademark of Omnesys, LLC and Omnesys Technologies, Inc. All rights reserved."
 * (the artwork itself stands in for the mark's name, so the text must NOT repeat it). Theme-swapped
 * and `onError`-guarded exactly like the badge row; `alt` carries the mark name for screen readers.
 */
export function PoweredByOmneMark({ heightClass = 'h-4' }: { heightClass?: string }) {
  return (
    <ArtworkBadge
      srcLight="/rithmic/powered-by-omne.png"
      srcDark="/rithmic/powered-by-omne-dark.png"
      alt="Powered by OMNE™"
      heightClass={heightClass}
      inline
    />
  );
}

/**
 * The "Trading Platform by Rithmic™" artwork on its own, rendered INLINE — sibling of
 * {@link PoweredByOmneMark}. The footer puts it at the LEFT of the R|Protocol API copyright notice
 * (user direction 2026-07-24), so Rithmic's mark sits with the Rithmic notice the way OMNE's sits
 * with the OMNE one, and each mark appears exactly once instead of in a separate badge row.
 */
export function TradingPlatformByRithmicMark({ heightClass = 'h-4' }: { heightClass?: string }) {
  return (
    <ArtworkBadge
      srcLight="/rithmic/trading-platform-by-rithmic.png"
      srcDark="/rithmic/trading-platform-by-rithmic-dark.png"
      alt="Trading Platform by Rithmic™"
      heightClass={heightClass}
      inline
    />
  );
}

/**
 * The badge row per Rithmic's mock: "Trading Platform by Rithmic™" left, "Powered by OMNE™" right,
 * with the full copyright notices in the hover title (and available as
 * {@link RITHMIC_COPYRIGHT_LINES} for surfaces that print them in full). `size` controls the artwork
 * height — 'md' (default) for the connect dialogs, 'sm' for the app-wide overlay badge.
 */
export function RithmicAttribution({ className = '', size = 'md' }: { className?: string; size?: 'sm' | 'md' }) {
  // Small footer marks (per Rithmic's mock) — kept modest so they don't compete with the form.
  // `md` = 18px, measured off the Add-account Figma (2026-07-25): both marks sit at ONE height there,
  // with the OMNE badge ~23% of the 516px content box (≈119px wide ⇒ 119/6.571 ≈ 18px tall). Was h-4.
  // NB the Figma's left mark is the wide `rithmic-logo` corporate lockup ("Market Data · Trading
  // Infrastructure"); we deliberately keep "Trading Platform by Rithmic" instead — conformance Q13, we
  // do not subscribe to Rithmic market data — so our left badge is narrower than the mock by design.
  const heightClass = size === 'md' ? 'h-[18px]' : 'h-3.5';
  return (
    <div
      className={`flex items-center justify-between gap-3 ${className}`}
      title={COPYRIGHT_TITLE}
      aria-label="Rithmic and OMNE attribution"
    >
      <ArtworkBadge
        srcLight="/rithmic/trading-platform-by-rithmic.png"
        srcDark="/rithmic/trading-platform-by-rithmic-dark.png"
        alt="Trading Platform by Rithmic™"
        heightClass={heightClass}
      />
      <ArtworkBadge
        srcLight="/rithmic/powered-by-omne.png"
        srcDark="/rithmic/powered-by-omne-dark.png"
        alt="Powered by OMNE™"
        heightClass={heightClass}
      />
    </div>
  );
}

// REMOVED 2026-07-25 (user direction): `RithmicAttributionOverlay` — the slim FIXED badge that floated
// in the bottom-right of the app while a Rithmic session was connecting/live. It was the original
// app-wide carrier for the conformance artwork; the marks now live in the TOP BAR (under the page
// title) and in the FOOTER, both gated on a Rithmic account being selected, so the floating copy was a
// third, redundant rendering. Do not reintroduce a floating badge without checking those two surfaces
// still satisfy "artwork on visible windows while using Rithmic's software" — see the coverage caveat
// in docs/features/rithmic.md.
